<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];

const integrationList = computed(
  () => getters['integrations/getAppIntegrations'].value
);

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

const accountId = computed(() => getters.getCurrentAccountId.value);
const metaCapiSettings = computed(
  () => getters.getCurrentAccount.value?.custom_attributes?.meta_capi || {}
);
const metaCapiEnabled = computed(() => Boolean(metaCapiSettings.value.enabled));
const metaCapiUrl = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/meta-capi`)
);
const metaCapiCard = {
  name: 'Meta Conversions API',
  description:
    'Envie eventos de conversão (Purchase / Lead) de volta para o Meta Ads Manager. Fecha o ciclo de atribuição de anúncios CTWA e melhora otimização de campanhas.',
};
// Template literal so Vite/Rollup don't try to resolve the public asset
// as a module at build time (same pattern as IntegrationItem.vue does).
const metaCapiLogoUrl = `/dashboard/images/integrations/meta_capi.svg`;
const showMetaCapiCard = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return true;
  return (
    metaCapiCard.name.toLowerCase().includes(query) ||
    metaCapiCard.description.toLowerCase().includes(query) ||
    'meta capi facebook instagram whatsapp conversions ctwa'.includes(query)
  );
});

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <span
          v-if="!filteredIntegrationList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <IntegrationItem
            v-for="item in filteredIntegrationList"
            :id="item.id"
            :key="item.id"
            :logo="item.logo"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
          <div
            v-if="showMetaCapiCard"
            class="flex flex-col flex-1 p-4 m-px outline outline-n-container outline-1 bg-n-card rounded-xl"
          >
            <div class="flex items-start justify-between">
              <div
                class="flex h-12 w-12 mb-2 items-center justify-center rounded-md border border-n-weak bg-n-alpha-3 dark:bg-n-alpha-2 p-2"
              >
                <img
                  :src="metaCapiLogoUrl"
                  alt="Meta"
                  class="max-h-full max-w-full"
                />
              </div>
              <Label
                :label="
                  metaCapiEnabled
                    ? $t('INTEGRATION_APPS.STATUS.ENABLED')
                    : $t('INTEGRATION_APPS.STATUS.DISABLED')
                "
                :color="metaCapiEnabled ? 'teal' : 'slate'"
                compact
              />
            </div>
            <h3 class="mb-1 text-base font-medium text-n-slate-12">
              {{ metaCapiCard.name }}
            </h3>
            <p
              class="mb-4 text-sm text-n-slate-11 line-clamp-2 min-h-[40px]"
            >
              {{ metaCapiCard.description }}
            </p>
            <div class="flex justify-end mt-auto">
              <router-link :to="metaCapiUrl">
                <Button
                  :label="$t('INTEGRATION_APPS.CONFIGURE')"
                  icon="i-woot-settings"
                  link
                  xs
                />
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
